<template>
	<div>
		<div class="row">
			<div class="col">
				<bread-crumb
						:breadcrumbData="breadcrumbData" />
			</div>
		</div>

		<div v-if="loading" class="report-loader">
			<div class="loader-message">Custom Report Generating</div>
			<div><h3 class="loader-submessage">This may take up to 1 minute.</h3></div>
					<div class="loader-container">
					<div class="loader-div">
						<img src="../../assets/equivateloader.gif" class="loader-image" alt="Loader">
				</div>
			</div>
		</div>

		<section class="mt-5" v-if="!loading && comingSoon">
			<div class="d-flex justify-content-between">
				<h1>Coming Soon!</h1>
			</div>
		</section>
		
		<section class="mt-5" v-if="!loading && !comingSoon">
				<div class="d-flex justify-content-between">
					<h3>Custom Report</h3>
					<ul class="list-inline">
						<li class="list-inline-item"><button class="btn btn-primary" @click="exportReport()">Export</button></li>
					</ul>
				</div>
			<div>
				<div class="alert alert-warning" role="alert">
					<strong>Beta Version</strong> — Data Validation in Progress
				</div>
				<ul class="nav nav-tabs mb-3" v-if="sheetNames.length > 1">
					<li class="nav-item" v-for="name in sheetNames" :key="name">
						<button class="nav-link" :class="{ active: activeSheet === name }" @click="selectSheet(name)">
							{{ name }}
						</button>
					</li>
				</ul>
				<EasyDataTable
					v-if="tableItems.length > 0"
					:key="activeSheet"
					:alternating="true"
					:rows-per-page="rows_per_page"
					:hide-footer="hideFooter"
					:headers="this.tableHeadings"
					:items="this.tableItems"
					:dense=true
					table-class-name="custom-table no-shadow"
				>
					<template #item-investment="{ investment, investment_id }">
						<router-link v-if="investment_id" :to="{ name: 'CompanyInvestment', params: { investmentId: investment_id } }">{{ investment }}</router-link>
						<span v-else>{{ investment }}</span>
					</template>
					<template #item-fund="{ fund, fund_id }">
						<router-link v-if="fund_id" :to="{ name: 'FundInvestment', params: { fundId: fund_id } }">{{ fund }}</router-link>
						<span v-else>{{ fund }}</span>
					</template>
					<!-- Aggregated metrics are click-through to their calculation. -->
					<template v-for="col in traceableKeys" #[col.slot]="item" :key="col.key">
						<button
							type="button"
							class="btn btn-link p-0 border-0 align-baseline metric-cell"
							:class="{ 'metric-cell--partial': isPartial(item, col.key) }"
							:title="traceTitle(item, col.key)"
							@click="openTrace(item, col.key)"
						>
							{{ item[col.key] }}<span v-if="isPartial(item, col.key)" class="metric-cell__flag">*</span>
						</button>
					</template>
				</EasyDataTable>
				<p v-if="hasPartialCells" class="text-muted small mt-2 mb-0">
					<span class="metric-cell__flag">*</span>
					Figures marked with an asterisk cover only part of the selected period — some months had no data.
					Click the value to see which months are missing.
				</p>
				<p v-if="tableItems.length === 0" class="text-muted mt-3">No {{ activeSheet }} data for selected investment(s).</p>
			</div>
		</section>
	</div>

	<!-- Drill-down. The trace comes from the aggregation itself, so it cannot disagree with the table. -->
	<div v-if="trace" class="modal fade show trace-modal" tabindex="-1" @click.self="closeTrace">
		<div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
			<div class="modal-content">
				<div class="modal-header">
					<div>
						<h5 class="modal-title mb-0">{{ trace.cell.label }}</h5>
						<small class="text-muted">{{ trace.context }}</small>
					</div>
					<button type="button" class="btn-close" aria-label="Close" @click="closeTrace"></button>
				</div>

				<div class="modal-body">
					<div class="d-flex justify-content-between align-items-baseline mb-2">
						<span class="badge bg-light text-dark">{{ ruleLabel(trace.cell.rule) }}</span>
						<code class="small">{{ trace.cell.formula }}</code>
					</div>

					<div v-if="trace.cell.value === null" class="alert alert-warning py-2 small">
						No value in any month of this period. Shown blank rather than zero.
					</div>

					<table v-if="trace.cell.contributions.length" class="table table-sm mb-0">
						<thead><tr><th>Month</th><th class="text-end">Value</th></tr></thead>
						<tbody>
							<tr v-for="c in trace.cell.contributions" :key="c.month">
								<td>{{ c.month }}</td>
								<td class="text-end">{{ formatTraceValue(trace.field, c.value) }}</td>
							</tr>
						</tbody>
						<tfoot>
							<tr class="fw-semibold border-top">
								<td>{{ trace.cell.rule === 'LAST' ? 'Period end' : 'Total' }}</td>
								<td class="text-end">{{ formatTraceValue(trace.field, trace.cell.value) }}</td>
							</tr>
						</tfoot>
					</table>

					<table v-if="trace.cell.operands.length" class="table table-sm mb-0">
						<tbody>
							<tr v-for="o in trace.cell.operands" :key="o.field">
								<td>{{ o.label }}</td>
								<td class="text-end">{{ formatTraceValue(o.field, o.value) }}</td>
							</tr>
						</tbody>
						<tfoot>
							<tr class="fw-semibold border-top">
								<td>Result</td>
								<td class="text-end">{{ formatTraceValue(trace.field, trace.cell.value) }}</td>
							</tr>
						</tfoot>
					</table>

					<div v-if="trace.cell.missingMonths.length" class="alert alert-warning py-2 small mt-3 mb-0">
						<strong>{{ trace.cell.monthsWithData }} of {{ trace.cell.monthsInPeriod }} months</strong>
						contributed. Missing: {{ trace.cell.missingMonths.join(', ') }}.
					</div>
				</div>

				<div class="modal-footer py-2">
					<small class="text-muted me-auto">Not included in the exported spreadsheet.</small>
					<button type="button" class="btn btn-secondary btn-sm" @click="closeTrace">Close</button>
				</div>
			</div>
		</div>
	</div>
	<div v-if="trace" class="modal-backdrop fade show"></div>

</template>

<script type="text/javascript">

	import BreadCrumb from '@/views/nav/BreadCrumb.vue'
	import { CustomExportApi } from '@/api/custom_export_api'

	const HEADER_ALL_CAPS = new Set(['IRR', 'MOIC', 'FMV', 'EBITDA', 'LTM', 'BCAP', 'TEV', 'CEO', 'CFO', 'RWI', 'QOE',
		'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'])

	// Internal join-key columns — present in row data for linking but hidden from the table header.
	const HIDDEN_COLS = new Set(['investment_id', 'fund_id'])

	const currencyFormatter = new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 0,
		maximumFractionDigits: 2
	})

	const currencyFormatterNoDecimals = new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0
	})

	function formatCurrency(v) {
		return Math.abs(v) >= 1_000_000_000
			? currencyFormatterNoDecimals.format(v)
			: currencyFormatter.format(v)
	}

	export default {
		name: 'ReportView',
		inheritAttrs: false,

		mixins: [],

		components: {
			BreadCrumb
		},

		filters: {
		},

		provide: function() {
			return {
			}
		},

		inject: [],

		props: ['funds', 'investments', 'exports', 'period', 'years', 'quarters', 'months', 'fields'],

		data() {
			return {
				exportData: {},
				activeSheet: null,
				loading: true,
				comingSoon: false,
				rows_per_page: 25,
				// Open drill-down: { field, cell, context }, or null when closed.
				trace: null
			}
		},

		computed: {
			breadcrumbData() {
				return [
					{
						linkText: 'Reports',
						routerLinkName: 'ReportsView'
					},
					{
						linkText: 'Custom Report'
					}
				]
			},

			sheetNames() {
				return Object.keys(this.exportData)
			},

			activeSheetData() {
				const data = this.exportData[this.activeSheet]
				if (!data) return { columnTypes: {}, rows: [] }
				return {
					columnTypes: data.columnTypes || {},
					rows: data.rows || [],
					rowTraces: data.rowTraces || null
				}
			},

			tableHeadings() {
				if (!this.activeSheetData.rows.length) return []
				const PINNED  = ['investment', 'fund']
				const allKeys = Object.keys(this.activeSheetData.rows[0]).filter(k => !HIDDEN_COLS.has(k))
				const pinned  = PINNED.filter(k => allKeys.includes(k))
				const rest    = allKeys.filter(k => !PINNED.includes(k))
				return [...pinned, ...rest].map(key => ({
					text: this.formatHeader(key),
					value: key,
					sortable: true,
					fixed: PINNED.includes(key),
					width: PINNED.includes(key) ? 160 : 130
				}))
			},

			tableItems() {
				const columnTypes = this.activeSheetData.columnTypes
				return this.activeSheetData.rows.map((row, rowIndex) => {
					// Headers derive from `rows`, not `items`, so this extra key
					// lets a cell find its trace without becoming a column.
					const item = { _rowIndex: rowIndex }
					Object.keys(row).forEach(key => {
						const v = row[key]
						const type = columnTypes[key]
						if (type === 'percent' && v != null) {
							item[key] = parseFloat(v).toFixed(2) + '%'
						} else if (type === 'multiple' && v != null) {
							item[key] = parseFloat(v).toFixed(2) + 'x'
						} else if (type === 'currency' && v != null) {
							item[key] = formatCurrency(v)
						} else if (type === 'date' && v) {
							item[key] = new Date(v + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
						} else {
							item[key] = v
						}
					})
					return item
				})
			},

			// Metric columns that carry a calculation trace. Empty for sheets that
			// are not aggregated, which leaves those cells rendering normally.
			traceableKeys() {
				const traces = this.activeSheetData.rowTraces
				if (!traces || !traces.length) return []
				const present = new Set()
				traces.forEach(t => Object.keys(t || {}).forEach(k => present.add(k)))
				// Slot name precomputed so the dynamic slot argument stays a plain
				// property access — no template literal inside an attribute name.
				return this.tableHeadings
					.map(h => h.value)
					.filter(k => present.has(k))
					.map(k => ({ key: k, slot: 'item-' + k }))
			},

			// Only surface the asterisk note when a cell on this sheet actually
			// carries one — an unexplained footnote is worse than none, and a
			// permanent one trains people to ignore it.
			hasPartialCells() {
				const traces = this.activeSheetData.rowTraces
				if (!traces || !traces.length) return false
				return traces.some(row => Object.values(row || {}).some(
					c => c && c.value != null && c.monthsWithData < c.monthsInPeriod
				))
			},

			// One definition of the selection, so the table and the download
			// cannot drift apart.
			selection() {
				return {
					funds: this.funds, investments: this.investments, exports: this.exports,
					period: this.period, years: this.years, quarters: this.quarters,
					months: this.months, fields: this.fields
				}
			},

			hideFooter() {
				return this.tableItems.length <= this.rows_per_page
			}
		},

		watch: {

		},

		created() {
		},

		async mounted() {
			const p = this.selection
			const exportTypes   = p.exports && p.exports !== '-' ? p.exports.split(',') : ['overview']
			const fundIds       = p.funds && p.funds !== '-' ? p.funds.split(',') : []
			const investmentIds = p.investments && p.investments !== '-' ? p.investments.split(',') : []
			try {
				const data = await CustomExportApi.fetchData(exportTypes, fundIds, investmentIds, p.period, p.years, p.quarters, p.months, p.fields)
				this.exportData = data || {}
				this.activeSheet = Object.keys(this.exportData)[0] || null
			} catch (err) {
				console.error('Failed to load export data:', err)
			} finally {
				this.loading = false
			}
		},

		methods: {
			// ── Drill-down ───────────────────────────────────────────────────
			traceFor(item, key) {
				const traces = this.activeSheetData.rowTraces
				if (!traces || item == null || item._rowIndex == null) return null
				return (traces[item._rowIndex] || {})[key] || null
			},

			// A cell is partial when some months of the period had no value. Fully
			// empty cells are not flagged — they render blank, which is self-evident.
			isPartial(item, key) {
				const cell = this.traceFor(item, key)
				return !!cell && cell.value != null && cell.monthsWithData < cell.monthsInPeriod
			},

			traceTitle(item, key) {
				return this.isPartial(item, key)
					? 'Partial period — click to see which months are missing'
					: 'Show calculation'
			},

			openTrace(item, key) {
				const raw = this.traceFor(item, key)
				if (!raw) return
				// Serde omits empty collections and nulls, so these keys are frequently
				// absent. Fill them in once here rather than guarding at every use.
				const cell = {
					value: null,
					contributions: [],
					operands: [],
					missingMonths: [],
					monthsWithData: 0,
					monthsInPeriod: 0,
					...raw
				}
				const parts = [item.investment, item.fund, item.period].filter(Boolean)
				this.trace = { field: key, cell, context: parts.join(' · ') }
			},

			closeTrace() {
				this.trace = null
			},

			ruleLabel(rule) {
				return {
					SUM: 'Sum of period',
					LTM_SUM: 'Sum of trailing 12 months',
					LAST: 'Value at period end',
					RATIO: 'Ratio of aggregated values',
					COMPOSITE: 'Sum of components'
				}[rule] || rule
			},

			// Formats using the column type of the field being shown, so operands
			// render in their own units rather than the result's.
			formatTraceValue(field, value) {
				if (value === null || value === undefined) return '—'
				const type = this.activeSheetData.columnTypes[field]
				if (type === 'percent') return (parseFloat(value) * 100).toFixed(2) + '%'
				if (type === 'multiple') return parseFloat(value).toFixed(2) + 'x'
				if (type === 'currency') return formatCurrency(value)
				return value
			},

				// Reads the same `selection` computed as the table, so a download can
			// never diverge from what is on screen.
			async exportReport() {
				const sel = this.selection
				const exportTypes   = sel.exports && sel.exports !== '-' ? sel.exports.split(',') : ['overview']
				const fundIds       = sel.funds && sel.funds !== '-' ? sel.funds.split(',') : []
				const investmentIds = sel.investments && sel.investments !== '-' ? sel.investments.split(',') : []
				try {
					await CustomExportApi.customExport(exportTypes, fundIds, investmentIds, sel.period, sel.years, sel.quarters, sel.months, sel.fields)
				} catch (err) {
					console.error('Export failed:', err)
				}
			},

			formatHeader(key) {
				return key.split('_').map(word => {
					if (!word) return word
					return HEADER_ALL_CAPS.has(word.toUpperCase())
						? word.toUpperCase()
						: word[0].toUpperCase() + word.slice(1)
				}).join(' ')
			},

			selectSheet(name) {
				this.activeSheet = name
			}
		}
	}
</script>

<style lang="scss">
@import '../../styles/custom-variables';
.report-options {
	background-color: $zebra;
}
.report-loader {
	width: 566px; 
	height: 200px;
	left: 50%;
	top: 50%;
	position: absolute;
    transform: translate(-50%,-50%);
}
.loader-message {
	width: 566px; 
	height: 25px; 
	left: 0px; 
	top: 84px; 
	position: absolute; 
	text-align: center; 
	color: $navy; 
	font-size: 24px;
}
.loader-submessage {
	width: 276px; 
	height: 71px; 
	left: 145px; 
	top: 129px; 
	position: absolute; 
	text-align: center;
	color: $darkblue !important;
}
.loader-container {
	width: 48px; 
	height: 48px; 
	left: 259px; 
	top: 0px; 
	position: absolute; 
	flex-direction: column; 
	justify-content: flex-start; 
	align-items: flex-start; 
	gap: 16px; 
	display: inline-flex
}
.loader-div {
	width: 48px; 
	height: 48px; 
	position: relative
}
.loader-image {
	width: 48px; 
	height: 48px; 
	display:block; 
	margin-left: auto; 
	margin-right: auto;
}

/* Aggregated metric cells are click-through to their calculation. Styled to
   read as ordinary table text until hovered, so the table stays scannable. */
.metric-cell {
	font: inherit;
	color: inherit;
	text-decoration: none;
	cursor: pointer;
}
.metric-cell:hover,
.metric-cell:focus-visible {
	text-decoration: underline dotted;
	text-underline-offset: 3px;
}
.metric-cell--partial {
	color: #b8860b;
}
.metric-cell__flag {
	margin-left: 1px;
	font-weight: 600;
}

/* Rendered without bootstrap's JS, so the display and stacking are set here. */
.modal.trace-modal {
	/* Two classes: Bootstrap's .modal sets display:none at equal specificity, so this must win regardless of stylesheet order. */
	display: block;
	background: transparent;
}
.modal.trace-modal .modal-content {
	box-shadow: 0 1rem 3rem rgba(0, 0, 0, .3);
}
.modal.trace-modal code {
	word-break: break-word;
}
</style>
